import { useState, useEffect } from "react";
import { api, imgSrc } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, AdminField } from "./shared";

const EMPTY_CABIN = {
    name: "", slug: "", tagline: "", description: "",
    price_per_night: 400, max_guests: 2, bedrooms: 1, bathrooms: 1, size_sqft: 500,
    amenities: [], images: [], featured: false, active: true,
};

export default function CabinsTab() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const refresh = () => api.get("/cabins?active=").then((r) => setItems(r.data));
    useEffect(() => { refresh(); }, []);

    const save = async (data) => {
        try {
            const payload = {
                ...data,
                price_per_night: Number(data.price_per_night),
                max_guests: Number(data.max_guests),
                bedrooms: Number(data.bedrooms),
                bathrooms: Number(data.bathrooms),
                size_sqft: Number(data.size_sqft),
                weekend_premium_pct: Number(data.weekend_premium_pct) || 0,
                seasons: (Array.isArray(data.seasons) ? data.seasons : []).map((s) => ({
                    name: String(s.name || "").trim(),
                    start: String(s.start || "").trim(),
                    end: String(s.end || "").trim(),
                    multiplier: Number(s.multiplier) || 1,
                    label: s.label ? String(s.label).trim() : null,
                })).filter((s) => s.name && s.start && s.end),
                amenities: Array.isArray(data.amenities) ? data.amenities : String(data.amenities || "").split(",").map((s) => s.trim()).filter(Boolean),
                images: Array.isArray(data.images) ? data.images : String(data.images || "").split(",").map((s) => s.trim()).filter(Boolean),
            };
            if (editing.id) await api.put(`/cabins/${editing.id}`, payload);
            else await api.post("/cabins", payload);
            toast.success("Cabin saved");
            setEditing(null);
            refresh();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this cabin?")) return;
        try { await api.delete(`/cabins/${id}`); toast.success("Deleted"); refresh(); } catch { toast.error("Delete failed"); }
    };

    return (
        <div data-testid="admin-cabins">
            <PageHeader
                eyebrow="Admin · Cabins"
                title="The collection."
                action={<button onClick={() => setEditing({ ...EMPTY_CABIN })} data-testid="cabins-new" className="inline-flex items-center gap-2 px-5 py-3 bg-forest text-bone text-xs uppercase tracking-widest2 font-semibold"><Plus size={14} /> New cabin</button>}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {items.map((c) => (
                    <div key={c.id} className="border border-espresso/15 overflow-hidden" data-testid={`admin-cabin-${c.slug}`}>
                        <div className="aspect-[4/3] bg-bone2 overflow-hidden"><img src={imgSrc(c.images?.[0])} alt={c.name} className="w-full h-full object-cover" /></div>
                        <div className="p-5">
                            <div className="flex justify-between items-start mb-1">
                                <h3 className="font-serif text-2xl">{c.name}</h3>
                                <span className="eyebrow text-espresso/70">${c.price_per_night}</span>
                            </div>
                            <p className="text-sm text-muted2 mb-4 line-clamp-2">{c.tagline}</p>
                            <div className="flex items-center gap-2 text-xs">
                                {c.featured && <span className="px-2 py-1 bg-terracotta/15 text-terracotta uppercase tracking-widest2">Featured</span>}
                                <span className={`px-2 py-1 uppercase tracking-widest2 ${c.active ? "bg-forest/10 text-forest" : "bg-espresso/15 text-espresso"}`}>{c.active ? "Active" : "Hidden"}</span>
                            </div>
                            <div className="flex gap-3 mt-5">
                                <button onClick={() => setEditing({ ...c, seasons: (Array.isArray(c.seasons) ? c.seasons : []).map((s) => ({ ...s, _k: Math.random().toString(36).slice(2) })) })} data-testid={`cabin-edit-${c.slug}`} className="text-xs uppercase tracking-widest2 text-forest hover:text-terracotta">Edit</button>
                                <button onClick={() => remove(c.id)} data-testid={`cabin-delete-${c.slug}`} className="text-xs uppercase tracking-widest2 text-terracotta hover:text-forest">Delete</button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {editing && (
                <CabinDialog data={editing} setData={setEditing} onSave={save} onClose={() => setEditing(null)} />
            )}
        </div>
    );
}

function CabinDialog({ data, setData, onSave, onClose }) {
    const { t } = useT();
    const update = (k, v) => setData({ ...data, [k]: v });
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-serif text-3xl">{t("admin.cabinDialogTitle", !!data.id)}</DialogTitle>
                    <DialogDescription>{t("admin.cabinDialogDesc")}</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <AdminField label="Name" value={data.name} onChange={(v) => update("name", v)} testid="cabin-form-name" />
                    <AdminField label="Slug" value={data.slug} onChange={(v) => update("slug", v)} testid="cabin-form-slug" />
                    <div className="md:col-span-2"><AdminField label="Tagline" value={data.tagline} onChange={(v) => update("tagline", v)} testid="cabin-form-tagline" /></div>
                    <div className="md:col-span-2">
                        <label className="eyebrow block mb-2">Description</label>
                        <textarea value={data.description} onChange={(e) => update("description", e.target.value)} rows={4} data-testid="cabin-form-description" className="w-full border border-espresso/20 p-3 text-sm bg-transparent" />
                    </div>
                    <AdminField label="Price / night" type="number" value={data.price_per_night} onChange={(v) => update("price_per_night", v)} testid="cabin-form-price" />
                    <AdminField label="Max guests" type="number" value={data.max_guests} onChange={(v) => update("max_guests", v)} testid="cabin-form-guests" />
                    <AdminField label="Bedrooms" type="number" value={data.bedrooms} onChange={(v) => update("bedrooms", v)} testid="cabin-form-bedrooms" />
                    <AdminField label="Bathrooms" type="number" value={data.bathrooms} onChange={(v) => update("bathrooms", v)} testid="cabin-form-baths" />
                    <AdminField label="Size sqft" type="number" value={data.size_sqft} onChange={(v) => update("size_sqft", v)} testid="cabin-form-sqft" />
                    <div className="md:col-span-2"><AdminField label="Amenities (comma separated)" value={Array.isArray(data.amenities) ? data.amenities.join(", ") : data.amenities} onChange={(v) => update("amenities", v)} testid="cabin-form-amenities" /></div>
                    <div className="md:col-span-2">
                        <CabinImageManager
                            images={Array.isArray(data.images) ? data.images : String(data.images || "").split(",").map((s) => s.trim()).filter(Boolean)}
                            onChange={(imgs) => update("images", imgs)}
                        />
                    </div>
                    <div className="md:col-span-2">
                        <SeasonalPricingEditor
                            weekendPct={Number(data.weekend_premium_pct) || 0}
                            onChangeWeekendPct={(v) => update("weekend_premium_pct", Number(v) || 0)}
                            seasons={Array.isArray(data.seasons) ? data.seasons : []}
                            onChangeSeasons={(s) => update("seasons", s)}
                        />
                    </div>
                    <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={!!data.featured} onChange={(e) => update("featured", e.target.checked)} data-testid="cabin-form-featured" /> Featured on home</label>
                    <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={!!data.active} onChange={(e) => update("active", e.target.checked)} data-testid="cabin-form-active" /> Active (visible to guests)</label>
                </div>
                <DialogFooter className="mt-6">
                    <button onClick={onClose} className="px-5 py-3 border border-espresso/30 text-xs uppercase tracking-widest2">Cancel</button>
                    <button onClick={() => onSave(data)} data-testid="cabin-form-save" className="px-5 py-3 bg-forest text-bone text-xs uppercase tracking-widest2 font-semibold">Save cabin</button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CabinImageManager({ images, onChange }) {
    const [uploading, setUploading] = useState(false);

    const uploadFiles = async (files) => {
        if (!files || files.length === 0) return;
        setUploading(true);
        const next = [...images];
        try {
            for (const file of files) {
                if (!file.type.startsWith("image/")) {
                    toast.error(`${file.name}: not an image`);
                    continue;
                }
                const fd = new FormData();
                fd.append("file", file);
                try {
                    const res = await api.post("/admin/uploads/cabin-image", fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    next.push(res.data.url);
                } catch (err) {
                    toast.error(err.response?.data?.detail || `${file.name}: upload failed`);
                }
            }
            onChange(next);
        } finally {
            setUploading(false);
        }
    };

    const onPickFile = (e) => {
        uploadFiles(Array.from(e.target.files || []));
        e.target.value = "";
    };

    const removeAt = (idx) => onChange(images.filter((_, i) => i !== idx));
    const moveUp = (idx) => {
        if (idx === 0) return;
        const next = [...images];
        [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
        onChange(next);
    };

    return (
        <div data-testid="cabin-image-manager">
            <label className="eyebrow block mb-2">Images</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                {images.map((img, idx) => (
                    <div key={`${img}-${idx}`} className="relative group border border-espresso/15 bg-bone2 aspect-[4/3] overflow-hidden" data-testid={`cabin-image-tile-${idx}`}>
                        <img src={imgSrc(img)} alt={`cabin ${idx}`} className="w-full h-full object-cover" />
                        {idx === 0 && (
                            <span className="absolute top-1 left-1 bg-forest text-bone text-[9px] uppercase tracking-widest2 px-1.5 py-0.5">Hero</span>
                        )}
                        <div className="absolute inset-x-0 bottom-0 bg-espresso/80 text-bone px-2 py-1 flex justify-between items-center opacity-0 group-hover:opacity-100 transition-opacity">
                            {idx > 0 ? (
                                <button type="button" onClick={() => moveUp(idx)} data-testid={`cabin-image-up-${idx}`} className="text-[10px] uppercase tracking-widest2 hover:text-terracotta">↑ Move</button>
                            ) : <span />}
                            <button type="button" onClick={() => removeAt(idx)} data-testid={`cabin-image-remove-${idx}`} className="text-[10px] uppercase tracking-widest2 hover:text-terracotta inline-flex items-center gap-1">
                                <X size={11} /> Remove
                            </button>
                        </div>
                    </div>
                ))}
                <label className="border border-dashed border-espresso/30 bg-bone2/40 aspect-[4/3] flex flex-col items-center justify-center cursor-pointer hover:bg-bone2 transition-colors text-xs uppercase tracking-widest2 text-espresso" data-testid="cabin-image-upload-dropzone">
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/avif" multiple onChange={onPickFile} className="hidden" data-testid="cabin-image-file-input" disabled={uploading} />
                    <Upload size={20} className="mb-2 text-forest" />
                    <span>{uploading ? "Uploading…" : "Upload"}</span>
                    <span className="text-[10px] text-muted2 mt-1 normal-case tracking-normal">PNG · JPG · WEBP · up to 8 MB</span>
                </label>
            </div>
            <p className="text-[11px] text-muted2 mt-2">Upload images from your device. The first image is the hero shown on cards and the cabin detail page; use “Move” to reorder.</p>
        </div>
    );
}

function SeasonalPricingEditor({ weekendPct, onChangeWeekendPct, seasons, onChangeSeasons }) {
    const updateSeason = (idx, patch) => {
        const next = seasons.map((s, i) => (i === idx ? { ...s, ...patch } : s));
        onChangeSeasons(next);
    };
    const removeSeason = (idx) => onChangeSeasons(seasons.filter((_, i) => i !== idx));
    const addSeason = () => onChangeSeasons([
        ...seasons,
        { name: "New season", start: "12-15", end: "01-15", multiplier: 1.2, label: "", _k: Math.random().toString(36).slice(2) },
    ]);

    return (
        <div data-testid="seasonal-pricing-editor" className="border border-espresso/15 p-4 bg-bone2/30">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="eyebrow">Seasonal & weekend pricing</div>
                    <p className="text-[11px] text-muted2 mt-1">Multipliers apply to the nightly rate. Use MM-DD for recurring yearly seasons (e.g. 12-15 → 01-15 wraps year-end).</p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-[11px] uppercase tracking-widest2 text-espresso/80">Weekend +%</label>
                    <input
                        type="number" min={0} max={100} step={5}
                        value={weekendPct}
                        onChange={(e) => onChangeWeekendPct(e.target.value)}
                        data-testid="cabin-form-weekend-pct"
                        className="w-20 border border-espresso/20 px-2 py-1.5 text-sm bg-transparent text-right"
                    />
                </div>
            </div>

            {seasons.length === 0 && (
                <p className="text-xs text-muted2 italic mb-3">No seasonal rates configured. Add a season to apply holiday or harvest multipliers.</p>
            )}

            <div className="space-y-2">
                {seasons.map((s, idx) => (
                    <div key={s._k ?? `season-${idx}`} className="grid grid-cols-12 gap-2 items-end" data-testid={`season-row-${idx}`}>
                        <div className="col-span-12 md:col-span-3">
                            <label className="block text-[10px] uppercase tracking-widest2 text-muted2 mb-1">Name</label>
                            <input type="text" value={s.name || ""} onChange={(e) => updateSeason(idx, { name: e.target.value })}
                                data-testid={`season-${idx}-name`}
                                className="w-full border border-espresso/20 px-2 py-1.5 text-sm bg-transparent" />
                        </div>
                        <div className="col-span-6 md:col-span-2">
                            <label className="block text-[10px] uppercase tracking-widest2 text-muted2 mb-1">Start (MM-DD)</label>
                            <input type="text" value={s.start || ""} onChange={(e) => updateSeason(idx, { start: e.target.value })}
                                placeholder="12-15"
                                data-testid={`season-${idx}-start`}
                                className="w-full border border-espresso/20 px-2 py-1.5 text-sm bg-transparent font-mono" />
                        </div>
                        <div className="col-span-6 md:col-span-2">
                            <label className="block text-[10px] uppercase tracking-widest2 text-muted2 mb-1">End (MM-DD)</label>
                            <input type="text" value={s.end || ""} onChange={(e) => updateSeason(idx, { end: e.target.value })}
                                placeholder="01-15"
                                data-testid={`season-${idx}-end`}
                                className="w-full border border-espresso/20 px-2 py-1.5 text-sm bg-transparent font-mono" />
                        </div>
                        <div className="col-span-6 md:col-span-2">
                            <label className="block text-[10px] uppercase tracking-widest2 text-muted2 mb-1">Multiplier</label>
                            <input type="number" step="0.05" min={0.5} max={3} value={s.multiplier ?? 1}
                                onChange={(e) => updateSeason(idx, { multiplier: Number(e.target.value) || 1 })}
                                data-testid={`season-${idx}-multiplier`}
                                className="w-full border border-espresso/20 px-2 py-1.5 text-sm bg-transparent text-right" />
                        </div>
                        <div className="col-span-6 md:col-span-2">
                            <label className="block text-[10px] uppercase tracking-widest2 text-muted2 mb-1">Label</label>
                            <input type="text" value={s.label || ""} onChange={(e) => updateSeason(idx, { label: e.target.value })}
                                placeholder="Holiday"
                                data-testid={`season-${idx}-label`}
                                className="w-full border border-espresso/20 px-2 py-1.5 text-sm bg-transparent" />
                        </div>
                        <div className="col-span-12 md:col-span-1 flex md:justify-end">
                            <button type="button" onClick={() => removeSeason(idx)}
                                data-testid={`season-${idx}-remove`}
                                className="text-[10px] uppercase tracking-widest2 text-terracotta hover:text-forest inline-flex items-center gap-1 px-2 py-1.5">
                                <X size={11} /> Remove
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <button type="button" onClick={addSeason} data-testid="season-add"
                className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest2 text-forest hover:text-terracotta">
                <Plus size={13} /> Add season
            </button>
        </div>
    );
}
